import React, { useState, useEffect } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Tag } from 'lucide-react';

const UpdateCategoryModal = ({ isOpen, onClose, onCategoryUpdated, category }) => {
  const [formData, setFormData] = useState({
    category_name: ''
  });
  
  const [image, setImage] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_BASE_URL = 'https://petifyshop.mycartly.in';

  // Populate form when category changes
  useEffect(() => {
    if (category) {
      setFormData({
        category_name: category.category_name || ''
      });

      // Set existing image URL
      if (category.image_path) {
        const timestamp = new Date().getTime();
        setExistingImageUrl(`${API_BASE_URL}${category.image_path}?t=${timestamp}`);
      } else {
        setExistingImageUrl(null);
      }
    }
  }, [category]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      setImage(null);
      setError('');
      return;
    }
    
    // Validate file size (400KB max)
    if (file.size > 400 * 1024) {
      setError('Image size must be less than 400KB');
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    
    setImage(file);
    setError('');
  };

  const removeNewImage = () => {
    setImage(null);
    // Reset file input
    const fileInput = document.getElementById('category-image-upload-update');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    if (!formData.category_name.trim()) {
      setError('Category name is required');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token missing. Please login again.');
        setLoading(false);
        return;
      }

      // Prepare FormData
      const submitData = new FormData();
      submitData.append('category_id', category.category_id);
      submitData.append('category_name', formData.category_name.trim());
      
      // Add new image if provided
      if (image) {
        submitData.append('image', image);
      }

      const response = await fetch(`${API_BASE_URL}/updatecategory`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Category updated successfully!');
        
        // Notify parent component
        if (onCategoryUpdated) {
          onCategoryUpdated();
        }
        
      } else {
        setError(data.error || 'Failed to update category');
      }
    } catch (networkError) {
      setError(`Network error: ${networkError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setImage(null);
      setError('');
      setSuccess('');
      
      // Reset file input
      const fileInput = document.getElementById('category-image-upload-update');
      if (fileInput) {
        fileInput.value = '';
      }
      
      onClose();
    }
  };

  if (!isOpen || !category) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Update Category</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          )}

          {/* Category Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Name *
            </label>
            <input
              type="text"
              name="category_name"
              value={formData.category_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter category name"
              disabled={loading}
              required
            />
          </div>

          {/* Current Image */}
          {existingImageUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Image
              </label>
              <div className="relative">
                <img
                  src={existingImageUrl}
                  alt="Current category"
                  className="w-full h-32 object-cover rounded-lg border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-32 bg-gray-100 rounded-lg hidden items-center justify-center border">
                  <Tag className="h-8 w-8 text-gray-400" />
                </div>
              </div>
            </div>
          )}

          {/* New Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {existingImageUrl ? 'Upload New Image (Optional)' : 'Category Image (Optional)'}
            </label>
            
            {!image ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="category-image-upload-update"
                  disabled={loading}
                />
                <label
                  htmlFor="category-image-upload-update"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Upload className="h-8 w-8 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {existingImageUrl ? 'Click to upload new image' : 'Click to upload image'}
                  </span>
                  <span className="text-xs text-gray-500">
                    PNG, JPG, JPEG, GIF, WEBP up to 400KB
                  </span>
                </label>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-2">New Image Preview:</p>
                <div className="relative">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="New category preview"
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={removeNewImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                    disabled={loading}
                  >
                    ×
                  </button>
                </div>
                {existingImageUrl && (
                  <p className="text-xs text-gray-500 mt-2">
                    This will replace the current image
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Update Category'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCategoryModal;